import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import CreateInvestorModal from '../components/CreateInvestorModal';
import AlertDialog from '../components/AlertDialog';
import FileList from '../components/FileList';
import DetailViewNavigation from '../components/DetailViewNavigation';
import { getApiUrl } from '../config/api.config';
import { investmentRoundAPI } from '../lib/api';

const InvestorDetail = () => {
    const { id, tenantId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [investor, setInvestor] = useState(null);
    const [investorDetails, setInvestorDetails] = useState(null);
    const [startups, setStartups] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [events, setEvents] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showLinkStartupModal, setShowLinkStartupModal] = useState(false);
    const [showInvestmentDetailsModal, setShowInvestmentDetailsModal] = useState(false);
    const [selectedInvestment, setSelectedInvestment] = useState(null);
    const [allStartups, setAllStartups] = useState([]);
    const [selectedStartupId, setSelectedStartupId] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);
    const [investmentDetails, setInvestmentDetails] = useState({
        investmentAmount: '',
        currency: 'INR',
        investmentDate: new Date().toISOString().split('T')[0],
        equityPercentage: '',
        entrySharePrice: '',
        entryShareEquity: '',
        numberOfShares: '',
        exitDate: '',
        exitValuation: '',
        investmentStage: 'SEED',
        investmentType: 'EQUITY',
        notes: ''
    });
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    // Investment Rounds state
    const [investorRounds, setInvestorRounds] = useState([]);
    const [selectedInvestorRound, setSelectedInvestorRound] = useState(null);

    useEffect(() => {
        loadInvestorDetails();
    }, [id]);

    // Update URL when tab changes
    useEffect(() => {
        setSearchParams({ tab: activeTab });
    }, [activeTab, setSearchParams]);

    // Sync activeTab when URL query param changes
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && ['overview', 'startups', 'events', 'files', 'investment-rounds'].includes(tabFromUrl)) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams]);

    useEffect(() => {
        if (showLinkStartupModal) {
            loadAllStartups();
        }
    }, [showLinkStartupModal]);

    // Reload events when startups change
    useEffect(() => {
        if (startups.length > 0 || activeTab === 'events') {
            loadRelatedEvents();
        }
    }, [startups.length, activeTab]);

    // Load investments when startups tab is active
    useEffect(() => {
        if (activeTab === 'startups' && id) {
            loadInvestments();
        }
    }, [activeTab, id]);

    // Load investment rounds when tab is active
    useEffect(() => {
        if (activeTab === 'investment-rounds' && id) {
            loadInvestorRounds();
        }
    }, [activeTab, id]);

    const loadInvestorRounds = async () => {
        try {
            const rounds = await investmentRoundAPI.getInvestorRounds(id);
            setInvestorRounds(rounds || []);
        } catch (error) {
            console.error('Failed to load investment rounds:', error);
            setInvestorRounds([]);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getRoundStatusBadge = (status) => {
        const colors = {
            OPEN: 'bg-green-100 text-green-800',
            CLOSED: 'bg-gray-100 text-gray-800',
            CANCELLED: 'bg-red-100 text-red-800',
            COMMITTED: 'bg-blue-100 text-blue-800',
            PARTIAL: 'bg-yellow-100 text-yellow-800',
            COMPLETED: 'bg-green-100 text-green-800',
            PENDING: 'bg-yellow-100 text-yellow-800',
            PAID: 'bg-green-100 text-green-800',
            OVERDUE: 'bg-red-100 text-red-800',
            ACTIVE: 'bg-green-100 text-green-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const loadInvestorDetails = async () => {
        setLoading(true);
        try {
            // Fetch investor user details
            const investorResponse = await fetch(getApiUrl(`admin/users/${id}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (!investorResponse.ok) {
                throw new Error('Failed to load investor');
            }

            const investorData = await investorResponse.json();
            setInvestor(investorData);

            // Fetch investor details and related data in parallel
            const [detailsResult, startupsResult, filesResult] = await Promise.allSettled([
                fetch(getApiUrl(`investor-network/user/${id}`), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }).then(res => res.ok ? res.json() : null),
                loadRelatedStartups(),
                loadRelatedFiles()
            ]);

            // Set investor details if available
            if (detailsResult.status === 'fulfilled' && detailsResult.value) {
                setInvestorDetails(detailsResult.value);
            }

            // Load events after startups are loaded
            await loadRelatedEvents();
        } catch (error) {
            console.error('Failed to load investor details:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadRelatedStartups = async () => {
        try {
            const portfolioResponse = await fetch(getApiUrl(`investments/investor/${id}/portfolio`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (portfolioResponse.ok) {
                const portfolio = await portfolioResponse.json();
                setStartups(portfolio || []);
            }
        } catch (error) {
            console.error('Failed to load related startups:', error);
            setStartups([]);
        }
    };

    const loadInvestments = async () => {
        try {
            const investmentsResponse = await fetch(getApiUrl(`investments/investor/${id}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (investmentsResponse.ok) {
                const investmentsData = await investmentsResponse.json();
                setInvestments(investmentsData || []);
            }
        } catch (error) {
            console.error('Failed to load investments:', error);
            setInvestments([]);
        }
    };

    const loadRelatedEvents = async () => {
        try {
            // Get all pitch events and filter by investor participation
            const eventsResponse = await fetch(getApiUrl('pitch-events'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (eventsResponse.ok) {
                const allEvents = await eventsResponse.json();
                // Filter events where this investor is an organizer or where their portfolio startups participate
                const relatedEvents = allEvents.filter(event => {
                    // Check if investor is organizer
                    if (event.organizerId === parseInt(id)) return true;
                    // Check if any portfolio startup is a participant
                    return startups.some(startup =>
                        event.participants?.some(p => p.startupId === startup.id || p.startup?.id === startup.id)
                    );
                });
                setEvents(relatedEvents);
            }
        } catch (error) {
            console.error('Failed to load related events:', error);
            setEvents([]);
        }
    };

    const loadRelatedFiles = async () => {
        try {
            const filesResponse = await fetch(getApiUrl(`files?moduleType=INVESTOR&moduleId=${id}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (filesResponse.ok) {
                const filesData = await filesResponse.json();
                setFiles(filesData || []);
            }
        } catch (error) {
            console.error('Failed to load related files:', error);
            setFiles([]);
        }
    };

    const loadAllStartups = async () => {
        try {
            const response = await fetch(getApiUrl('admin/users/startups'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAllStartups(data || []);
            }
        } catch (error) {
            console.error('Failed to load startups:', error);
            setAllStartups([]);
        }
    };

    const handleLinkStartup = async () => {
        if (!selectedStartupId) {
            setAlert({ isOpen: true, title: 'Error', message: 'Please select a startup', type: 'error' });
            return;
        }

        if (!investmentDetails.investmentAmount || parseFloat(investmentDetails.investmentAmount) <= 0) {
            setAlert({ isOpen: true, title: 'Error', message: 'Please enter a valid investment amount', type: 'error' });
            return;
        }

        setLinkLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            const investmentData = {
                investorId: id,
                startupId: selectedStartupId,
                investmentDate: investmentDetails.investmentDate ? new Date(investmentDetails.investmentDate).toISOString() : new Date().toISOString(),
                investmentAmount: parseFloat(investmentDetails.investmentAmount),
                currency: investmentDetails.currency || 'INR',
                investmentStage: investmentDetails.investmentStage || 'SEED',
                investmentType: investmentDetails.investmentType || 'EQUITY',
                autoGrantAccess: true,
                accessLevel: 'FULL'
            };

            // Add optional fields if provided
            if (investmentDetails.equityPercentage) {
                investmentData.equityPercentage = parseFloat(investmentDetails.equityPercentage);
            }
            if (investmentDetails.entrySharePrice) {
                investmentData.entrySharePrice = parseFloat(investmentDetails.entrySharePrice);
            }
            if (investmentDetails.entryShareEquity) {
                investmentData.entryShareEquity = parseFloat(investmentDetails.entryShareEquity);
            }
            if (investmentDetails.numberOfShares) {
                investmentData.numberOfShares = parseInt(investmentDetails.numberOfShares);
            }
            if (investmentDetails.exitDate) {
                investmentData.exitDate = new Date(investmentDetails.exitDate).toISOString();
            }
            if (investmentDetails.exitValuation) {
                investmentData.exitValuation = parseFloat(investmentDetails.exitValuation);
            }
            if (investmentDetails.notes) {
                investmentData.notes = investmentDetails.notes;
            }

            const response = await fetch(getApiUrl('investments'), {
                method: 'POST',
                headers,
                body: JSON.stringify(investmentData)
            });

            if (response.ok) {
                setAlert({
                    isOpen: true,
                    title: 'Success',
                    message: 'Startup linked successfully!',
                    type: 'success'
                });
                setShowLinkStartupModal(false);
                setSelectedStartupId('');
                setInvestmentDetails({
                    investmentAmount: '',
                    currency: 'INR',
                    investmentDate: new Date().toISOString().split('T')[0],
                    equityPercentage: '',
                    entrySharePrice: '',
                    entryShareEquity: '',
                    numberOfShares: '',
                    exitDate: '',
                    exitValuation: '',
                    investmentStage: 'SEED',
                    investmentType: 'EQUITY',
                    notes: ''
                });
                // Reload related startups and investments
                await loadRelatedStartups();
                await loadInvestments();
            } else {
                const errorData = await response.json();
                setAlert({
                    isOpen: true,
                    title: 'Error',
                    message: errorData.message || 'Failed to link startup',
                    type: 'error'
                });
            }
        } catch (error) {
            console.error('Failed to link startup:', error);
            setAlert({
                isOpen: true,
                title: 'Error',
                message: 'Failed to link startup. Please try again.',
                type: 'error'
            });
        } finally {
            setLinkLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.ok) {
                setAlert({ isOpen: true, title: 'Success', message: 'Investor deleted successfully!', type: 'success' });
                setTimeout(() => {
                    navigate(`/${tenantId}/investors`);
                }, 1000);
            } else {
                setAlert({ isOpen: true, title: 'Error', message: 'Failed to delete investor', type: 'error' });
            }
        } catch (error) {
            console.error('Failed to delete investor:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to delete investor', type: 'error' });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!investor) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900">Investor not found</h2>
                    <button onClick={() => navigate(`/${tenantId}/investors`)} className="mt-4 text-blue-600 hover:text-blue-800">
                        ← Back to Investors
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Page Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate(`/${tenantId}/investors`)}
                                className="text-gray-600 hover:text-gray-900"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">{investor.name}</h1>
                                <p className="text-sm text-gray-500 mt-1">Individual Investor</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            {/* Navigation */}
                            <DetailViewNavigation type="investor" currentId={id} />

                            {/* Action Buttons */}
                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setShowEditModal(true)}
                                    className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50 transition"
                                    title="Edit Investor"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition"
                                    title="Delete Investor"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                {/* Tabs */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            {[
                                { id: 'overview', label: 'Overview' },
                                { id: 'startups', label: 'Startups' },
                                { id: 'investment-rounds', label: 'Investment Rounds' },
                                { id: 'events', label: 'Events' },
                                { id: 'files', label: 'Files' }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <>
                            {/* Investor Details */}
                            <div className="lg:col-span-1">
                                <div className="bg-white rounded-lg shadow p-4 space-y-4">
                                    <h2 className="text-lg font-semibold text-gray-900">Investor Details</h2>

                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Email</label>
                                        <p className="mt-1 text-gray-900">{investor.email}</p>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Phone</label>
                                        <p className="mt-1 text-gray-900">{investor.phoneNumber || 'N/A'}</p>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Job Title</label>
                                        <p className="mt-1 text-gray-900">{investor.jobTitle || 'N/A'}</p>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Department</label>
                                        <p className="mt-1 text-gray-900">{investor.department || 'N/A'}</p>
                                    </div>

                                    {investorDetails && (
                                        <>
                                            {investorDetails.investorType && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Investor Type</label>
                                                    <p className="mt-1 text-gray-900">{investorDetails.investorType}</p>
                                                </div>
                                            )}
                                            {investorDetails.companyName && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Company</label>
                                                    <p className="mt-1 text-gray-900">{investorDetails.companyName}</p>
                                                </div>
                                            )}
                                            {investorDetails.geographicFocus && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Geographic Focus</label>
                                                    <p className="mt-1 text-gray-900">{investorDetails.geographicFocus}</p>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Status</label>
                                        <p className="mt-1">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                {investor.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Information */}
                            <div className="lg:col-span-2">
                                <div className="bg-white rounded-lg shadow p-4">
                                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>

                                    {investorDetails ? (
                                        <div className="space-y-4">
                                            {investorDetails.investmentSectors && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Investment Sectors</label>
                                                    <p className="mt-1 text-gray-900">{investorDetails.investmentSectors}</p>
                                                </div>
                                            )}
                                            {investorDetails.preferredStages && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Preferred Stages</label>
                                                    <p className="mt-1 text-gray-900">{investorDetails.preferredStages}</p>
                                                </div>
                                            )}
                                            {investorDetails.minInvestmentAmount && investorDetails.maxInvestmentAmount && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Investment Range</label>
                                                    <p className="mt-1 text-gray-900">
                                                        ${investorDetails.minInvestmentAmount.toLocaleString()} - ${investorDetails.maxInvestmentAmount.toLocaleString()}
                                                    </p>
                                                </div>
                                            )}
                                            {investorDetails.physicalAddress && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Address</label>
                                                    <p className="mt-1 text-gray-900">{investorDetails.physicalAddress}</p>
                                                </div>
                                            )}
                                            {investorDetails.nationality && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Nationality</label>
                                                    <p className="mt-1 text-gray-900">{investorDetails.nationality}</p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500">No additional details available</p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Startups Tab */}
                    {activeTab === 'startups' && (
                        <div className="lg:col-span-3">
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Portfolio Startups</h2>
                                        <p className="text-sm text-gray-500 mt-1">Startups where this investor has invested</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowLinkStartupModal(true);
                                            setSelectedStartupId('');
                                            setInvestmentDetails({
                                                investmentAmount: '',
                                                currency: 'INR',
                                                investmentDate: new Date().toISOString().split('T')[0],
                                                equityPercentage: '',
                                                entrySharePrice: '',
                                                entryShareEquity: '',
                                                exitDate: '',
                                                exitValuation: '',
                                                investmentStage: 'SEED',
                                                investmentType: 'EQUITY',
                                                notes: ''
                                            });
                                        }}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span>Link Startup</span>
                                    </button>
                                </div>

                                {startups.length === 0 ? (
                                    <div className="p-12 text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No startups yet</h3>
                                        <p className="mt-1 text-sm text-gray-500">Startups will appear here once investments are made.</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-200">
                                        {startups.map((startup) => {
                                            const investment = investments.find(inv => inv.startupId === startup.id);
                                            return (
                                                <div key={startup.id} className="px-4 py-3 hover:bg-gray-50 transition">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3 flex-1">
                                                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center">
                                                                <span className="text-white font-medium">
                                                                    {(startup.organizationName || startup.name)?.charAt(0)?.toUpperCase()}
                                                                </span>
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="text-sm font-medium text-gray-900">{startup.organizationName || startup.name}</p>
                                                                <p className="text-xs text-gray-500">{startup.email}</p>
                                                                {investment && (
                                                                    <p className="text-xs text-blue-600 mt-1">
                                                                        Invested: {investment.currency} {investment.investmentAmount?.toLocaleString() || 'N/A'}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            {investment && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedInvestment(investment);
                                                                        setShowInvestmentDetailsModal(true);
                                                                    }}
                                                                    className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                                                                >
                                                                    View Details
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => navigate(`/${tenantId}/startups/${startup.id}`)}
                                                                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                                                            >
                                                                View Startup
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Events Tab */}
                    {activeTab === 'events' && (
                        <div className="lg:col-span-3">
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-4 py-3 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900">Related Events</h2>
                                    <p className="text-sm text-gray-500 mt-1">Pitch events where this investor is an organizer or where portfolio startups participate</p>
                                </div>

                                {events.length === 0 ? (
                                    <div className="p-12 text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No events yet</h3>
                                        <p className="mt-1 text-sm text-gray-500">Events will appear here when this investor participates in pitch events.</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-200">
                                        {events.map((event) => (
                                            <div key={event.id} className="px-4 py-3 hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate(`/${tenantId}/events/${event.id}`)}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
                                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{event.eventName}</p>
                                                            <p className="text-xs text-gray-500">
                                                                {event.eventDate ? new Date(event.eventDate).toLocaleDateString() : 'Date TBD'} • {event.location || 'Location TBD'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${event.status === 'UPCOMING' ? 'bg-blue-100 text-blue-800' :
                                                            event.status === 'ONGOING' ? 'bg-green-100 text-green-800' :
                                                                event.status === 'COMPLETED' ? 'bg-gray-100 text-gray-800' :
                                                                    'bg-red-100 text-red-800'
                                                            }`}>
                                                            {event.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Files Tab */}
                    {activeTab === 'files' && (
                        <div className="lg:col-span-3">
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-4 py-3 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900">Related Files</h2>
                                    <p className="text-sm text-gray-500 mt-1">Files associated with this investor</p>
                                </div>
                                <div className="p-4">
                                    <FileList
                                        files={files}
                                        onFileDeleted={() => loadRelatedFiles()}
                                        showActions={true}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Investment Rounds Tab */}
                    {activeTab === 'investment-rounds' && (
                        <div className="lg:col-span-3">
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-4 py-3 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900">Investment Rounds</h2>
                                    <p className="text-sm text-gray-500 mt-1">Rounds where this investor has participated</p>
                                </div>
                                <div className="p-4">
                                    {investorRounds.length === 0 ? (
                                        <div className="text-center py-12">
                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No investment rounds</h3>
                                            <p className="mt-1 text-sm text-gray-500">This investor hasn't participated in any funding rounds yet.</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                            {/* Rounds List */}
                                            <div className="space-y-3">
                                                {investorRounds.map(round => (
                                                    <div
                                                        key={round.id}
                                                        onClick={() => setSelectedInvestorRound(round)}
                                                        className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                                                            selectedInvestorRound?.id === round.id ? 'bg-blue-50 border-blue-300' : ''
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <h4 className="font-semibold text-gray-900">{round.roundName}</h4>
                                                                <p className="text-sm text-gray-500">{round.startupName}</p>
                                                            </div>
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoundStatusBadge(round.status)}`}>
                                                                {round.status}
                                                            </span>
                                                        </div>
                                                        <div className="mt-2 flex items-center justify-between text-sm">
                                                            <span className="text-gray-600">
                                                                Participation: {formatCurrency(round.participationAmount)}
                                                            </span>
                                                        </div>
                                                        {round.targetAmount > 0 && (
                                                            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                                                <div
                                                                    className="bg-blue-600 h-2 rounded-full"
                                                                    style={{ width: `${Math.min(round.progressPercentage || 0, 100)}%` }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Round Details */}
                                            <div>
                                                {selectedInvestorRound ? (
                                                    <div className="bg-gray-50 rounded-lg border p-4">
                                                        <h3 className="text-lg font-bold text-gray-900 mb-4">
                                                            {selectedInvestorRound.roundName} - {selectedInvestorRound.startupName}
                                                        </h3>
                                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                                            <div>
                                                                <p className="text-gray-500">Target Amount</p>
                                                                <p className="font-semibold">{formatCurrency(selectedInvestorRound.targetAmount)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-500">Raised Amount</p>
                                                                <p className="font-semibold">{formatCurrency(selectedInvestorRound.raisedAmount)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-500">Your Participation</p>
                                                                <p className="font-semibold text-blue-600">{formatCurrency(selectedInvestorRound.participationAmount)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-500">Share Price</p>
                                                                <p className="font-semibold">{formatCurrency(selectedInvestorRound.sharePrice)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-500">Shares Allocated</p>
                                                                <p className="font-semibold">{selectedInvestorRound.sharesAllocated?.toLocaleString() || '-'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-500">Equity %</p>
                                                                <p className="font-semibold">{selectedInvestorRound.equityPercentage ? `${selectedInvestorRound.equityPercentage}%` : '-'}</p>
                                                            </div>
                                                        </div>
                                                        {selectedInvestorRound.terms && selectedInvestorRound.terms.length > 0 && (
                                                            <div className="mt-4 pt-4 border-t">
                                                                <h4 className="font-medium text-gray-700 mb-2">Payment Terms</h4>
                                                                <div className="space-y-2">
                                                                    {selectedInvestorRound.terms.map(term => (
                                                                        <div key={term.id} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                                                                            <div>
                                                                                <p className="font-medium">Term #{term.termNumber}</p>
                                                                                <p className="text-gray-500 text-xs">Due: {term.dueDate || 'Not set'}</p>
                                                                            </div>
                                                                            <div className="flex items-center space-x-2">
                                                                                <span className="font-semibold">{formatCurrency(term.amount)}</span>
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs ${getRoundStatusBadge(term.status)}`}>
                                                                                    {term.status}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="bg-gray-50 rounded-lg border p-8 text-center">
                                                        <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <p className="text-gray-500">Select a round to view details</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <CreateInvestorModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        loadInvestorDetails();
                    }}
                    initialType="INVESTOR"
                    initialData={investor}
                    isEdit={true}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Investor?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete "{investor.name}"? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Link Startup Modal */}
            {showLinkStartupModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 rounded-t-xl">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-bold text-white">Link Startup</h2>
                                <button
                                    onClick={() => {
                                        setShowLinkStartupModal(false);
                                        setSelectedStartupId('');
                                        setInvestmentDetails({
                                            investmentAmount: '',
                                            currency: 'INR',
                                            investmentDate: new Date().toISOString().split('T')[0],
                                            equityPercentage: '',
                                            entrySharePrice: '',
                                            entryShareEquity: '',
                                            exitDate: '',
                                            exitValuation: '',
                                            investmentStage: 'SEED',
                                            investmentType: 'EQUITY',
                                            notes: ''
                                        });
                                    }}
                                    className="text-white hover:text-gray-200 transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Startup Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Startup <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedStartupId}
                                    onChange={(e) => setSelectedStartupId(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">-- Select a Startup --</option>
                                    {allStartups
                                        .filter(startup => !startups.some(s => s.id === startup.id))
                                        .map((startup) => (
                                            <option key={startup.id} value={startup.id}>
                                                {startup.organizationName || startup.name} ({startup.email})
                                            </option>
                                        ))}
                                </select>
                                {allStartups.filter(startup => !startups.some(s => s.id === startup.id)).length === 0 && (
                                    <p className="text-sm text-gray-500 mt-2">
                                        All available startups are already linked to this investor.
                                    </p>
                                )}
                            </div>

                            {/* Investment Details Section */}
                            {selectedStartupId && (
                                <div className="border-t pt-4 mt-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment Details</h3>

                                    {/* Basic Investment Info */}
                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Investment Amount <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={investmentDetails.investmentAmount}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, investmentAmount: e.target.value })}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="1000000"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Currency <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={investmentDetails.currency}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, currency: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="INR">INR (₹)</option>
                                                <option value="USD">USD ($)</option>
                                                <option value="EUR">EUR (€)</option>
                                                <option value="GBP">GBP (£)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Investment Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={investmentDetails.investmentDate}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, investmentDate: e.target.value })}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Investment Stage <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={investmentDetails.investmentStage}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, investmentStage: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="SEED">Seed</option>
                                                <option value="SERIES_A">Series A</option>
                                                <option value="SERIES_B">Series B</option>
                                                <option value="SERIES_C">Series C</option>
                                                <option value="GROWTH">Growth</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Investment Type <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={investmentDetails.investmentType}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, investmentType: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="EQUITY">Equity</option>
                                                <option value="CONVERTIBLE_NOTE">Convertible Note</option>
                                                <option value="SAFE">SAFE</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Equity Percentage (%)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                value={investmentDetails.equityPercentage}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, equityPercentage: e.target.value })}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="10.0"
                                            />
                                        </div>
                                    </div>

                                    {/* Entry Options */}
                                    <div className="mb-6">
                                        <h4 className="text-md font-semibold text-gray-900 mb-3">Entry Options</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Entry Share Price
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={investmentDetails.entrySharePrice}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, entrySharePrice: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="100.00"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Entry Share Equity (%)
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="100"
                                                    value={investmentDetails.entryShareEquity}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, entryShareEquity: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="10.0"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    No. of CCPS
                                                </label>
                                                <input
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    value={investmentDetails.numberOfShares}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, numberOfShares: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="1000"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Exit Options */}
                                    <div className="mb-6">
                                        <h4 className="text-md font-semibold text-gray-900 mb-3">Exit Options</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Exit Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={investmentDetails.exitDate}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, exitDate: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Exit Valuation
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={investmentDetails.exitValuation}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, exitValuation: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="5000000"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Notes
                                        </label>
                                        <textarea
                                            value={investmentDetails.notes}
                                            onChange={(e) => setInvestmentDetails({ ...investmentDetails, notes: e.target.value })}
                                            rows="3"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Additional notes about this investment..."
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    onClick={() => {
                                        setShowLinkStartupModal(false);
                                        setSelectedStartupId('');
                                        setInvestmentDetails({
                                            investmentAmount: '',
                                            currency: 'INR',
                                            investmentDate: new Date().toISOString().split('T')[0],
                                            equityPercentage: '',
                                            entrySharePrice: '',
                                            entryShareEquity: '',
                                            numberOfShares: '',
                                            exitDate: '',
                                            exitValuation: '',
                                            investmentStage: 'SEED',
                                            investmentType: 'EQUITY',
                                            notes: ''
                                        });
                                    }}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                    disabled={linkLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleLinkStartup}
                                    disabled={linkLoading || !selectedStartupId}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                >
                                    {linkLoading && (
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    )}
                                    <span>{linkLoading ? 'Linking...' : 'Link Startup'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Investment Details Modal */}
            {showInvestmentDetailsModal && selectedInvestment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-bold text-white">Investment Details</h2>
                                    <p className="text-sm text-blue-100 mt-1">{selectedInvestment.startupName}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowInvestmentDetailsModal(false);
                                        setSelectedInvestment(null);
                                    }}
                                    className="text-white hover:text-gray-200 transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Investment Summary */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment Summary</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="text-xs font-medium text-gray-500 uppercase">Amount</label>
                                        <p className="text-lg font-bold text-gray-900 mt-1">
                                            {selectedInvestment.currency} {selectedInvestment.investmentAmount?.toLocaleString() || 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-500 uppercase">Date</label>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {selectedInvestment.investmentDate ? new Date(selectedInvestment.investmentDate).toLocaleDateString() : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-500 uppercase">Stage</label>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {selectedInvestment.investmentStage || 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-500 uppercase">Type</label>
                                        <p className="text-sm font-medium text-gray-900 mt-1">
                                            {selectedInvestment.investmentType || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Entry Details */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    </svg>
                                    Entry Details
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Entry Share Price</label>
                                        <p className="text-base font-semibold text-gray-900 mt-1">
                                            {selectedInvestment.currency} {selectedInvestment.entrySharePrice?.toLocaleString() || 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Entry Share Equity</label>
                                        <p className="text-base font-semibold text-gray-900 mt-1">
                                            {selectedInvestment.entryShareEquity ? `${selectedInvestment.entryShareEquity}%` : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Equity Percentage</label>
                                        <p className="text-base font-semibold text-gray-900 mt-1">
                                            {selectedInvestment.equityPercentage ? `${selectedInvestment.equityPercentage}%` : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">No. of CCPS</label>
                                        <p className="text-base font-semibold text-gray-900 mt-1">
                                            {selectedInvestment.numberOfShares?.toLocaleString() || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Exit Details */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                    </svg>
                                    Exit Details
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Exit Date</label>
                                        <p className="text-base font-semibold text-gray-900 mt-1">
                                            {selectedInvestment.exitDate ? new Date(selectedInvestment.exitDate).toLocaleDateString() : 'Not Exited'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Exit Valuation</label>
                                        <p className="text-base font-semibold text-gray-900 mt-1">
                                            {selectedInvestment.exitValuation ? `${selectedInvestment.currency} ${selectedInvestment.exitValuation.toLocaleString()}` : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Exit Multiple</label>
                                        <p className="text-base font-semibold text-gray-900 mt-1">
                                            {selectedInvestment.exitMultiple ? `${selectedInvestment.exitMultiple}x` : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                {selectedInvestment.exitDate && (
                                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <p className="text-sm text-green-800">
                                            <span className="font-semibold">Status:</span> This investment has been exited.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Additional Information */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-500">Status</span>
                                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${selectedInvestment.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                                            selectedInvestment.status === 'EXITED' ? 'bg-gray-100 text-gray-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {selectedInvestment.status || 'N/A'}
                                        </span>
                                    </div>
                                    {selectedInvestment.leadInvestor && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-500">Lead Investor</span>
                                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                Yes
                                            </span>
                                        </div>
                                    )}
                                    {selectedInvestment.boardSeat && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-500">Board Seat</span>
                                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                                Yes
                                            </span>
                                        </div>
                                    )}
                                    {selectedInvestment.accessLevel && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-500">Access Level</span>
                                            <span className="text-sm font-medium text-gray-900">{selectedInvestment.accessLevel}</span>
                                        </div>
                                    )}
                                    {selectedInvestment.notes && (
                                        <div>
                                            <span className="text-sm font-medium text-gray-500 block mb-2">Notes</span>
                                            <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedInvestment.notes}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    onClick={() => navigate(`/${tenantId}/startups/${selectedInvestment.startupId}`)}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                                >
                                    View Startup Profile
                                </button>
                                <button
                                    onClick={() => {
                                        setShowInvestmentDetailsModal(false);
                                        setSelectedInvestment(null);
                                    }}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />
        </>
    );
};

export default InvestorDetail;
